import React, { useState, useEffect } from 'react';
import { getReference, getFilePreviewUrl, getFileDownloadUrl } from '../api';

const formatSize = (bytes) => {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
};

const cleanNom = (nom) => nom
  .replace(/^ACTOR\s+/i, '')
  .replace(/^SCENE\s+/i, '')
  .split(' ')
  .map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
  .join(' ');

const fileIcon = (type) => {
  if (type === 'pdf') return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
      <polyline points="14 2 14 8 20 8"/>
      <line x1="9" y1="13" x2="15" y2="13"/>
    </svg>
  );
  if (type === 'image') return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="3" y="3" width="18" height="18" rx="2"/>
      <circle cx="8.5" cy="8.5" r="1.5"/>
      <polyline points="21 15 16 10 5 21"/>
    </svg>
  );
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M13 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z"/>
      <polyline points="13 2 13 9 20 9"/>
    </svg>
  );
};

export default function Detail({ reference, onBack }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedFile, setSelectedFile] = useState(null);
  const [hoveredFile, setHoveredFile] = useState(null);

  useEffect(() => {
    getReference(reference)
      .then(res => {
        setData(res.data);
        const firstPdf = res.data.fichiers?.find(f => f.type_fichier === 'pdf');
        const firstFile = firstPdf || res.data.fichiers?.[0];
        if (firstFile) setSelectedFile(firstFile);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [reference]);

  const pdfFiles = data?.fichiers?.filter(f => f.type_fichier === 'pdf') || [];
  const imageFiles = data?.fichiers?.filter(f => f.type_fichier === 'image') || [];
  const otherFiles = data?.fichiers?.filter(f => f.type_fichier === 'autre') || [];

  const renderPreview = () => {
    if (!selectedFile) return null;
    const url = getFilePreviewUrl(selectedFile.id);

    if (selectedFile.type_fichier === 'pdf') {
      return (
        <iframe
          key={selectedFile.id} src={url}
          style={{ width: '100%', height: '100%', border: 'none', minHeight: '75vh' }}
          title={selectedFile.nom_fichier}
        />
      );
    }
    if (selectedFile.type_fichier === 'image') {
      return (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', padding: 24 }}>
          <img
            key={selectedFile.id} src={url}
            alt={selectedFile.nom_fichier}
            style={{
              maxWidth: '100%', maxHeight: '75vh',
              objectFit: 'contain', borderRadius: 4,
              boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
            }}
          />
        </div>
      );
    }
    return (
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        height: '100%', flexDirection: 'column', gap: 16, color: 'var(--text-muted)'
      }}>
        <div style={{ fontSize: 48 }}>📎</div>
        <div style={{ fontSize: 14 }}>Aperçu non disponible</div>
        <a href={getFileDownloadUrl(selectedFile.id)} download={selectedFile.nom_fichier}>
          <button style={{
            padding: '10px 24px', background: 'var(--accent)', border: 'none',
            color: '#fff', borderRadius: 4, cursor: 'pointer', fontSize: 13
          }}>Télécharger le fichier</button>
        </a>
      </div>
    );
  };

  const FileItem = ({ file }) => {
    const isSelected = selectedFile?.id === file.id;
    const isHovered = hoveredFile === file.id;
    return (
      <div
        onClick={() => setSelectedFile(file)}
        onMouseEnter={() => setHoveredFile(file.id)}
        onMouseLeave={() => setHoveredFile(null)}
        style={{
          padding: '12px 16px', cursor: 'pointer',
          display: 'flex', alignItems: 'flex-start', gap: 12,
          borderLeft: `3px solid ${isSelected ? 'var(--accent)' : 'transparent'}`,
          background: isSelected ? 'var(--accent-light)' : isHovered ? 'var(--bg-3)' : 'transparent',
          transition: 'all 0.15s', borderBottom: '1px solid var(--border)'
        }}
      >
        <span style={{ color: isSelected ? 'var(--accent)' : 'var(--text-muted)', marginTop: 2, flexShrink: 0 }}>
          {fileIcon(file.type_fichier)}
        </span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            fontSize: 12, color: isSelected ? 'var(--accent)' : 'var(--text-dim)',
            lineHeight: 1.4, wordBreak: 'break-word', fontWeight: isSelected ? 500 : 400
          }}>{file.nom_fichier}</div>
          {file.taille_bytes > 0 && (
            <div style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--text-light)', marginTop: 3 }}>
              {formatSize(file.taille_bytes)}
            </div>
          )}
        </div>
        <a
          href={getFileDownloadUrl(file.id)}
          download={file.nom_fichier}
          onClick={e => e.stopPropagation()}
          title="Télécharger"
        >
          <button style={{
            padding: '4px 8px', background: 'none',
            border: '1px solid var(--border)', borderRadius: 3,
            color: 'var(--text-muted)', cursor: 'pointer', fontSize: 11,
            flexShrink: 0, transition: 'all 0.15s'
          }}
            onMouseEnter={e => { e.target.style.borderColor = 'var(--accent)'; e.target.style.color = 'var(--accent)'; }}
            onMouseLeave={e => { e.target.style.borderColor = 'var(--border)'; e.target.style.color = 'var(--text-muted)'; }}
          >↓</button>
        </a>
      </div>
    );
  };

  const SectionHeader = ({ label, count }) => (
    <div style={{
      padding: '10px 16px 6px',
      fontFamily: 'var(--font-mono)', fontSize: 9,
      letterSpacing: 3, color: 'var(--text-muted)',
      textTransform: 'uppercase', background: 'var(--bg-3)',
      borderBottom: '1px solid var(--border)',
      display: 'flex', justifyContent: 'space-between', alignItems: 'center'
    }}>
      <span>{label}</span>
      <span style={{ background: 'var(--border-dark)', color: 'var(--text-dim)', padding: '1px 6px', borderRadius: 8, fontSize: 10 }}>{count}</span>
    </div>
  );

  if (loading) return (
    <div style={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg)' }}>
      <div style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--text-muted)', letterSpacing: 4 }}>CHARGEMENT...</div>
    </div>
  );

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <header style={{
        background: 'var(--bg-2)', borderBottom: '1px solid var(--border)',
        padding: '0 40px', height: 64,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        position: 'sticky', top: 0, zIndex: 100, boxShadow: 'var(--shadow-sm)'
      }}>
        <button onClick={onBack} style={{
          display: 'flex', alignItems: 'center', gap: 8,
          background: 'none', border: 'none', cursor: 'pointer',
          color: 'var(--text-muted)', fontSize: 14, fontFamily: 'var(--font-body)',
          transition: 'color 0.2s'
        }}
          onMouseEnter={e => e.currentTarget.style.color = 'var(--accent)'}
          onMouseLeave={e => e.currentTarget.style.color = 'var(--text-muted)'}
        >
          ← Retour
        </button>

        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <span style={{
            fontFamily: 'var(--font-mono)', fontSize: 16,
            fontWeight: 600, color: 'var(--accent)', letterSpacing: 1
          }}>{reference}</span>
          <span style={{
            background: 'var(--bg-3)', border: '1px solid var(--border)',
            padding: '3px 12px', borderRadius: 12,
            fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--text-muted)'
          }}>
            {data?.fichiers?.length} fichier{data?.fichiers?.length !== 1 ? 's' : ''}
          </span>
        </div>

        {selectedFile && (
          <a href={getFileDownloadUrl(selectedFile.id)} download={selectedFile.nom_fichier}>
            <button style={{
              padding: '9px 20px', background: 'var(--accent)', border: 'none',
              color: '#fff', borderRadius: 4, cursor: 'pointer',
              fontSize: 13, fontWeight: 500, transition: 'background 0.2s'
            }}
              onMouseEnter={e => e.target.style.background = '#6d1414'}
              onMouseLeave={e => e.target.style.background = 'var(--accent)'}
            >↓ Télécharger</button>
          </a>
        )}
      </header>

      <div style={{ flex: 1, display: 'flex', height: 'calc(100vh - 64px)' }}>
        {/* Sidebar */}
        <aside style={{
          width: 320, minWidth: 320,
          background: 'var(--bg-2)', borderRight: '1px solid var(--border)',
          display: 'flex', flexDirection: 'column', overflow: 'hidden'
        }}>
          {/* Acteurs */}
          <div style={{ padding: '20px 16px', borderBottom: '1px solid var(--border)' }}>
            <div style={{
              fontFamily: 'var(--font-mono)', fontSize: 9, letterSpacing: 3,
              color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: 12
            }}>Acteurs identifiés</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {data?.acteurs?.length > 0
                ? data.acteurs.map(a => (
                  <span key={a.id} style={{
                    padding: '4px 12px',
                    background: 'var(--accent-light)',
                    border: '1px solid #f5c6c6',
                    color: 'var(--accent)', borderRadius: 16,
                    fontSize: 13, fontWeight: 500
                  }}>{cleanNom(a.nom)}</span>
                ))
                : <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>Non identifiés</span>
              }
            </div>
          </div>

          {/* Liste fichiers */}
          <div style={{ flex: 1, overflowY: 'auto' }}>
            {pdfFiles.length > 0 && (<><SectionHeader label="PDF" count={pdfFiles.length} />{pdfFiles.map(f => <FileItem key={f.id} file={f} />)}</>)}
            {imageFiles.length > 0 && (<><SectionHeader label="Images" count={imageFiles.length} />{imageFiles.map(f => <FileItem key={f.id} file={f} />)}</>)}
            {otherFiles.length > 0 && (<><SectionHeader label="Autres" count={otherFiles.length} />{otherFiles.map(f => <FileItem key={f.id} file={f} />)}</>)}
          </div>
        </aside>

        {/* Zone de preview */}
        <div style={{ flex: 1, background: 'var(--bg-3)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {selectedFile ? (
            <>
              <div style={{
                padding: '12px 24px', background: 'var(--bg-2)',
                borderBottom: '1px solid var(--border)',
                display: 'flex', alignItems: 'center', gap: 12
              }}>
                <span style={{ color: 'var(--text-muted)' }}>{fileIcon(selectedFile.type_fichier)}</span>
                <span style={{
                  fontSize: 13, color: 'var(--text-dim)',
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1
                }}>{selectedFile.nom_fichier}</span>
                {selectedFile.taille_bytes > 0 && (
                  <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--text-muted)', flexShrink: 0 }}>
                    {formatSize(selectedFile.taille_bytes)}
                  </span>
                )}
              </div>
              <div style={{ flex: 1, overflow: 'auto' }}>
                {renderPreview()}
              </div>
            </>
          ) : (
            <div style={{
              flex: 1, display: 'flex', alignItems: 'center',
              justifyContent: 'center', flexDirection: 'column', gap: 12
            }}>
              <div style={{ fontSize: 48, opacity: 0.3 }}>📄</div>
              <div style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--text-light)', letterSpacing: 3 }}>
                SÉLECTIONNER UN FICHIER
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
